import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import TagFilterBar from "./Components/TagFilterBar";
import Spinner from "../../globalComponents/ui/Spinner";
import SimpleDropdown from "../../globalComponents/form/dropdowns/SimpleDropdown";
import ViewOnlyBanner from "./Components/ViewOnlyBanner";
import { getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseTags(raw) {
  if (!raw) return [];
  let parsed = raw;
  if (typeof raw === "string") { try { parsed = JSON.parse(raw); } catch { return []; } }
  if (!Array.isArray(parsed) || parsed.length === 0) return [];
  if (Array.isArray(parsed[0])) return parsed;
  return [parsed];
}

function getNextEligibleFilters(allQuestions, filterSteps) {
  const active = filterSteps.filter(s => s.values?.size);
  const kvm = new Map();
  for (const q of allQuestions) {
    for (const combo of parseTags(q.tags)) {
      if (!combo?.length) continue;
      if (!active.length) {
        const t = combo[0];
        if (t?.key && t?.value) {
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
        continue;
      }
      let pos = 0, matched = true;
      for (const step of active) {
        if (pos >= combo.length || combo[pos].key !== step.key || !step.values.has(combo[pos].value)) { matched = false; break; }
        pos++;
      }
      if (matched && pos < combo.length) {
        const t = combo[pos];
        if (t?.key && t?.value) {
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
      }
    }
  }
  return Array.from(kvm.entries()).map(([key, values]) => ({ key, values: Array.from(values).sort() }));
}

function questionMatchesSteps(q, steps) {
  const active = steps.filter(s => s.values && s.values.size > 0);
  if (!active.length) return true;
  const combos = parseTags(q.tags);
  return combos.some(c => {
    let pos = 0;
    for (const step of active) {
      let found = false;
      for (let i = pos; i < c.length; i++) {
        if (c[i]?.key === step.key && step.values.has(c[i]?.value)) { pos = i + 1; found = true; break; }
      }
      if (!found) return false;
    }
    return true;
  });
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────



function Badge({ children, color = "#64748b", bg = "#f1f5f9", border = "#e2e8f0" }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
      background: bg, color, border: `1px solid ${border}`, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// ─── Factor Change Warning Modal ──────────────────────────────────────────────

function FactorWarningModal({ unitName, onConfirm, onCancel }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, width: "min(400px, 94vw)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        {/* Icon strip */}
        <div style={{ background: "#fffbeb", padding: "20px 24px 16px", borderBottom: "1px solid #fef3c7", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>⚠</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
              Editing conversion factor
            </div>
            <div style={{ fontSize: 12, color: "#b45309", lineHeight: 1.5 }}>
              Changing the factor for <strong>"{unitName}"</strong> will affect how historical data
              is converted and displayed across all reports.
            </div>
          </div>
        </div>

        <div style={{ padding: "16px 24px 20px" }}>
          <p style={{ fontSize: 12, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
            This action may cause inconsistencies in previously recorded values.
            Are you sure you want to continue?
          </p>
        </div>

        <div style={{ padding: "0 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "1.5px solid #e2e8f0",
              background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#64748b",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "none",
              background: "#d97706", color: "#fff",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
            }}
          >
            Yes, edit factor
          </button>
        </div>
      </div>
    </div>
  );
}



// ─── Unit Editor Modal ────────────────────────────────────────────────────────
// platformBaseUnitId → locked disabled input, factor always locked
// selectedUnitId     → user's display unit, fully selectable
// Changing any non-base factor triggers a confirmation warning

function UnitEditorModal({ kpi, onClose, onSave }) {
  const [localUnits, setLocalUnits] = useState(() =>
    (kpi.supportedUnits || []).map(u => ({ ...u }))
  );
  const [selectedUnitId, setSelectedUnitId] = useState(kpi.selectedUnitId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Warning state: which unit's factor is pending a confirmed edit
  const [pendingFactorEdit, setPendingFactorEdit] = useState(null);
  // { unit_id, unitName, newValue }

  const baseUnit = localUnits.find(u => u.unit_id === kpi.platformBaseUnitId);

  const handleFactorChange = (unit_id, unitName, value) => {
    setPendingFactorEdit({ unit_id, unitName, newValue: value });
  };

  const confirmFactorEdit = () => {
    if (!pendingFactorEdit) return;
    setLocalUnits(prev => prev.map(u =>
      u.unit_id === pendingFactorEdit.unit_id
        ? { ...u, factor: pendingFactorEdit.newValue }
        : u
    ));
    setPendingFactorEdit(null);
  };

  const cancelFactorEdit = () => setPendingFactorEdit(null);

  const validate = () => {
    for (const u of localUnits) {
      if (u.unit_id === kpi.platformBaseUnitId) continue;
      const f = Number(u.factor);
      if (u.factor === "" || isNaN(f) || f <= 0)
        return `Factor for "${u.unit ?? u.unit_id}" must be a positive number.`;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSaving(true);
    const cleaned = localUnits.map(u => ({
      unit_id: u.unit_id,
      factor: Number(u.factor),
    }));
    await onSave({
      kpiId: kpi.kpiId,
      platformBaseUnitId: kpi.platformBaseUnitId,
      selectedUnitId,
      supportedUnits: cleaned,
    });
    setSaving(false);
  };

  const disabledInputStyle = {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: "1.5px solid #e2e8f0", fontSize: 13,
    background: "#f8fafc", color: "#94a3b8",
    cursor: "not-allowed", outline: "none",
    fontWeight: 500,
  };

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "#fff", borderRadius: 14, width: "min(520px, 96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>

          {/* Header */}
          <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
                {kpi.title}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>Configure selected unit &amp; conversion factors</div>
            </div>
            <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 18, color: "#94a3b8", cursor: "pointer", lineHeight: 1, padding: "2px 4px", flexShrink: 0 }}>×</button>
          </div>

          <div style={{ overflowY: "auto", padding: "18px 20px", flex: 1, display: "flex", flexDirection: "column", gap: 18 }}>

            {/* Platform Base Unit — disabled input field */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Platform Base Unit
              </label>
              <input
                disabled
                value={baseUnit?.unit ?? `Unit #${kpi.platformBaseUnitId}`}
                style={disabledInputStyle}
              />
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
                This is set by the platform and cannot be changed.
              </div>
            </div>

            {/* Selected Unit */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Selected Unit
              </label>
              <div style={{ position: "relative", maxWidth: 280 }}>
                <select
                  value={selectedUnitId ?? ""}
                  onChange={e => setSelectedUnitId(Number(e.target.value))}
                  style={{
                    width: "100%", padding: "8px 32px 8px 10px", borderRadius: 8,
                    border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none",
                    background: "#fff", color: "#1e293b", appearance: "none", cursor: "pointer",
                  }}
                  onFocus={e => e.target.style.borderColor = "#93c5fd"}
                  onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                >
                  {localUnits.map(u => (
                    <option key={u.unit_id} value={u.unit_id}>{u.unit ?? `Unit #${u.unit_id}`}</option>
                  ))}
                </select>
                {/* Small inline chevron */}
                <span style={{
                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  pointerEvents: "none", fontSize: 10, color: "#94a3b8", lineHeight: 1,
                }}>▾</span>
              </div>
            </div>

            {/* Conversion factor table */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Conversion Factors
              </div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                {/* Header */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "8px 14px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
                  {["Unit", "Factor"].map((h, i) => (
                    <span key={i} style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</span>
                  ))}
                </div>

                {localUnits.map((u, idx) => {
                  const isBase = u.unit_id === kpi.platformBaseUnitId;
                  const isSelected = u.unit_id === selectedUnitId;
                  const isLast = idx === localUnits.length - 1;

                  return (
                    <div key={u.unit_id} style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr",
                      padding: "10px 14px", borderBottom: isLast ? "none" : "1px solid #f8fafc",
                      alignItems: "center",
                      background: isBase ? "#fafafa" : "#fff",
                    }}>
                      {/* Unit name + badges */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: isBase ? "#94a3b8" : "#1e293b" }}>
                          {u.unit ?? `#${u.unit_id}`}
                        </span>
                        {isBase && (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: "#e2e8f0", color: "#64748b" }}>BASE</span>
                        )}
                        {isSelected && !isBase && (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: "#dbeafe", color: "#1d4ed8" }}>SELECTED</span>
                        )}
                      </div>

                      {/* Factor cell */}
                      <div>
                        {isBase ? (
                          // Base unit factor — disabled input, cannot edit
                          <input
                            disabled
                            value={u.factor}
                            style={{
                              width: "80%", padding: "6px 8px", borderRadius: 7,
                              border: "1.5px solid #e2e8f0",
                              background: "#f1f5f9", color: "#94a3b8",
                              fontSize: 13, fontWeight: 500,
                              cursor: "not-allowed", outline: "none",
                            }}
                          />
                        ) : (
                          // Editable factor — warns before applying
                          <input
                            type="number"
                            step="any"
                            min="0.000001"
                            value={u.factor}
                            onChange={e => handleFactorChange(u.unit_id, u.unit ?? `Unit #${u.unit_id}`, e.target.value)}
                            style={{
                              width: "80%", padding: "6px 8px", borderRadius: 7,
                              border: "1.5px solid #e2e8f0",
                              fontSize: 13, outline: "none",
                              background: "#fff", color: "#1e293b",
                              cursor: "text",
                            }}
                            onFocus={e => e.target.style.borderColor = "#93c5fd"}
                            onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: "#94a3b8", margin: "6px 0 0" }}>
                Factors represent how many base units equal one of the given unit.
              </p>
            </div>

            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#dc2626" }}>
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#64748b" }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: saving ? "#e2e8f0" : "#3f88a5",
              color: saving ? "#94a3b8" : "#fff",
              cursor: saving ? "default" : "pointer", fontSize: 13, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
              boxShadow: saving ? "none" : "0 2px 8px rgba(63,136,165,0.3)",
            }}>
              {saving ? <><Spinner size={12} color="#94a3b8" /> Saving…</> : "Save Configuration"}
            </button>
          </div>
        </div>
      </div>

      {pendingFactorEdit && (
        <FactorWarningModal
          unitName={pendingFactorEdit.unitName}
          onConfirm={confirmFactorEdit}
          onCancel={cancelFactorEdit}
        />
      )}
    </>
  );
}

// ─── Bulk Apply Modal ─────────────────────────────────────────────────────────

function BulkApplyModal({ filteredKpis, onClose, onApply }) {
  const [targetUnitId, setTargetUnitId] = useState("");
  const [applying, setApplying] = useState(false);

  const availableUnits = useMemo(() => {
    const map = new Map();
    for (const kpi of filteredKpis) {
      if (!kpi.hasUnits) continue;
      for (const u of kpi.supportedUnits) {
        if (u.unit_id && !map.has(u.unit_id))
          map.set(u.unit_id, { unit_id: u.unit_id, unit: u.unit });
      }
    }
    return Array.from(map.values());
  }, [filteredKpis]);

  const selectedUnitIdNum = targetUnitId ? Number(targetUnitId) : null;

  const eligibleKpis = useMemo(() => {
    if (!selectedUnitIdNum) return [];
    return filteredKpis.filter(k =>
      k.hasUnits && k.supportedUnits.some(u => u.unit_id === selectedUnitIdNum)
    );
  }, [filteredKpis, selectedUnitIdNum]);

  const handleApply = async () => {
    if (!selectedUnitIdNum || !eligibleKpis.length) return;
    setApplying(true);
    const configs = eligibleKpis.map(k => ({
      kpiId: k.kpiId,
      platformBaseUnitId: k.platformBaseUnitId,
      selectedUnitId: selectedUnitIdNum,
      supportedUnits: k.supportedUnits.map(u => ({ unit_id: u.unit_id, factor: u.factor })),
    }));
    await onApply(configs);
    setApplying(false);
  };

  const kpisWithUnits = filteredKpis.filter(k => k.hasUnits).length;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "min(420px, 96vw)", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "18px 24px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Bulk Apply Selected Unit</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
              Apply to {kpisWithUnits} visible KPI{kpisWithUnits !== 1 ? "s" : ""} with units
            </div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, color: "#94a3b8", cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Select Unit
            </label>
            <div style={{ position: "relative" }}>
              <select
                value={targetUnitId}
                onChange={e => setTargetUnitId(e.target.value)}
                style={{
                  width: "100%", padding: "9px 36px 9px 12px", borderRadius: 8,
                  border: "1.5px solid #e2e8f0", fontSize: 13,
                  outline: "none", background: "#fff", color: "#1e293b",
                  cursor: "pointer", appearance: "none",
                }}
              >
                <option value="">— Choose a unit —</option>
                {availableUnits.map(u => (
                  <option key={u.unit_id} value={u.unit_id}>{u.unit}</option>
                ))}
              </select>
              <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 10, color: "#94a3b8", lineHeight: 1 }}>▾</span>
            </div>
          </div>

          {selectedUnitIdNum && (
            <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "12px 16px" }}>
              <div style={{ fontSize: 12, color: "#0c4a6e" }}>
                Will update <strong>{eligibleKpis.length} KPI{eligibleKpis.length !== 1 ? "s" : ""}</strong>
                {eligibleKpis.length < kpisWithUnits && (
                  <span style={{ fontSize: 11, color: "#64748b" }}> — others skipped (unit not supported)</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#64748b" }}>
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedUnitIdNum || !eligibleKpis.length || applying}
            style={{
              padding: "9px 22px", borderRadius: 8, border: "none",
              background: (!selectedUnitIdNum || !eligibleKpis.length) ? "#e2e8f0" : "#3f88a5",
              color: (!selectedUnitIdNum || !eligibleKpis.length) ? "#94a3b8" : "#fff",
              cursor: (!selectedUnitIdNum || !eligibleKpis.length) ? "default" : "pointer",
              fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8,
            }}
          >
            {applying
              ? <><Spinner size={12} color="#94a3b8" /> Applying…</>
              : `Apply to ${eligibleKpis.length} KPI${eligibleKpis.length !== 1 ? "s" : ""}`
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Row ──────────────────────────────────────────────────────────────────

function KpiUnitRow({ kpi, pendingConfig, onConfigure, onQuickUnitChange, isLast, disabled }) {
  const cfg = pendingConfig ?? kpi;
  const isDirty = !disabled && !!pendingConfig;

  const allTags = useMemo(() => {
    const combos = parseTags(kpi.tags);
    const seen = new Set(); const result = [];
    for (const combo of combos)
      for (const t of combo) {
        if (!t?.key || !t?.value) continue;
        const k = `${t.key}:::${t.value}`;
        if (!seen.has(k)) { seen.add(k); result.push(t); }
      }
    return result;
  }, [kpi.tags]);

  if (!kpi.hasUnits) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "11px 18px",
        borderBottom: isLast ? "none" : "1px solid #f1f5f9", background: "#fafafa",
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
            {kpi.title}
          </div>
        </div>
        <Badge>N/A</Badge>
      </div>
    );
  }

  const displayUnits = cfg.supportedUnits ?? kpi.supportedUnits;
  const displaySelectedId = cfg.selectedUnitId ?? kpi.selectedUnitId;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14, padding: "11px 18px",
      borderBottom: isLast ? "none" : "1px solid #f1f5f9",
      background: isDirty ? "#fffbeb" : "#fff", transition: "background 0.1s",
    }}>
      {/* Title + tags */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
          {kpi.title}
        </div>
        {allTags.length > 0 && (
          <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
            {allTags.slice(0, 4).map((t, i) => (
              <span key={i} style={{ fontSize: 10, color: "#475569", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 4, padding: "1px 6px" }}>{t.value}</span>
            ))}
            {allTags.length > 4 && <span style={{ fontSize: 10, color: "#94a3b8" }}>+{allTags.length - 4}</span>}
          </div>
        )}
      </div>

      {/* Quick selected unit selector — no label, with chevron */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <select
          value={displaySelectedId ?? ""}
          onChange={e => onQuickUnitChange(kpi.kpiId, Number(e.target.value))}
          disabled={disabled}
          style={{
            padding: "6px 30px 6px 10px", borderRadius: 7,
            border: `1.5px solid ${isDirty ? "#fcd34d" : "#e2e8f0"}`,
            background: disabled ? "#f8fafc" : (isDirty ? "#fffbeb" : "#fff"),
            fontSize: 12, fontWeight: 600,
            color: disabled ? "#94a3b8" : (isDirty ? "#b45309" : "#1e293b"),
            cursor: disabled ? "not-allowed" : "pointer", outline: "none",
            appearance: "none", minWidth: 80,
          }}
        >
          {displayUnits.map(u => (
            <option key={u.unit_id} value={u.unit_id}>{u.unit}</option>
          ))}
        </select>
        <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 10, color: disabled ? "#cbd5e1" : (isDirty ? "#b45309" : "#94a3b8"), lineHeight: 1 }}>▾</span>
      </div>

      {/* Unsaved indicator — plain text, no pill */}
      {isDirty && (
        <span style={{ fontSize: 11, fontWeight: 600, color: "#d97706", flexShrink: 0 }}>
          Unsaved
        </span>
      )}

      {/* Configure button */}
      {!disabled && (
        <button
          onClick={() => onConfigure(kpi)}
          style={{
            padding: "6px 14px", borderRadius: 7, border: "1.5px solid #e2e8f0",
            background: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#3f88a5",
            display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#e0f2fe"; e.currentTarget.style.borderColor = "#93c5fd"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
        >
          ⚙ Configure
        </button>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function UnitKpiSettings() {
  const [allKpis, setAllKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [pendingConfigs, setPendingConfigs] = useState(new Map());
  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [editingKpi, setEditingKpi] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fyData = useMemo(() => { try { return JSON.parse(localStorage.getItem("financialYears")) ?? []; } catch { return []; } }, []);
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);

  const isViewOnly = selectedFY?.id !== latestFY?.id;

  const fetchKpis = useCallback(async () => {
    if (!selectedFY?.id) return;
    setLoading(true);
    try {
      const [settingsRes, unitSettingsRes] = await Promise.all([
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getUnitKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
      ]);

      if (unitSettingsRes?.isSuccess && unitSettingsRes?.data?.data) {
        const rawSettings = settingsRes?.data;
        const allUnitKpis = unitSettingsRes.data.data;
        
        let filteredBySettings = allUnitKpis;
        if (rawSettings && Array.isArray(rawSettings.kpiIds)) {
          const enabledKpiIds = new Set(rawSettings.kpiIds.map(Number));
          filteredBySettings = allUnitKpis.filter(k => enabledKpiIds.has(Number(k.kpiId)));
        } else {
          // If no settings saved yet for this FY, default to empty because no KPIs are selected in KPIs settings
          filteredBySettings = [];
        }

        setAllKpis(filteredBySettings);
        setPendingConfigs(new Map());
      } else {
        showToast("Failed to load KPI unit settings", "error");
      }
    } catch {
      showToast("Error loading KPI unit settings", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFY?.id]);

  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  const [kpiView, setKpiView] = useState("list");

  const filteredKpis = useMemo(() => {
    let list = allKpis.filter(k => questionMatchesSteps(k, filterSteps));
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(k => k.title?.toLowerCase().includes(s));
    }
    return list;
  }, [allKpis, filterSteps, search]);

  const selectedKpisCount = useMemo(() => {
    return allKpis.filter(kpi => {
      const cfg = pendingConfigs.get(kpi.kpiId) ?? kpi;
      return cfg.selectedUnitId !== null && cfg.selectedUnitId !== undefined;
    }).length;
  }, [allKpis, pendingConfigs]);

  const displayKpis = useMemo(() => {
    if (kpiView === "list") return filteredKpis;
    return filteredKpis.filter(kpi => {
      const cfg = pendingConfigs.get(kpi.kpiId) ?? kpi;
      return cfg.selectedUnitId !== null && cfg.selectedUnitId !== undefined;
    });
  }, [filteredKpis, kpiView, pendingConfigs]);

  const handleQuickUnitChange = (kpiId, newSelectedUnitId) => {
    const kpi = allKpis.find(k => k.kpiId === kpiId);
    if (!kpi) return;
    const base = pendingConfigs.get(kpiId) ?? kpi;
    setPendingConfigs(prev => new Map(prev).set(kpiId, {
      kpiId,
      platformBaseUnitId: base.platformBaseUnitId,
      selectedUnitId: newSelectedUnitId,
      supportedUnits: base.supportedUnits,
    }));
  };

  const handleModalSave = async (cfg) => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}saveUnitKpiSettings`,
        {},
        { configs: [cfg] },
        "POST"
      );
      if (isSuccess) {
        setAllKpis(prev => prev.map(k => {
          if (k.kpiId !== cfg.kpiId) return k;
          const newUnitMap = new Map(cfg.supportedUnits.map(u => [u.unit_id, u.factor]));
          return {
            ...k,
            selectedUnitId: cfg.selectedUnitId,
            selectedUnit: k.supportedUnits.find(u => u.unit_id === cfg.selectedUnitId)?.unit ?? k.selectedUnit,
            supportedUnits: k.supportedUnits.map(u => ({
              ...u,
              factor: newUnitMap.has(u.unit_id) ? newUnitMap.get(u.unit_id) : u.factor,
            })),
            isCustomised: true,
          };
        }));
        setPendingConfigs(prev => { const n = new Map(prev); n.delete(cfg.kpiId); return n; });
        setEditingKpi(null);
        showToast("Unit configuration saved");
      } else {
        showToast(data?.message || "Failed to save", "error");
      }
    } catch {
      showToast("Error saving configuration", "error");
    }
  };

  const handleBulkApply = async (configs) => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}saveUnitKpiSettings`,
        {},
        { configs },
        "POST"
      );
      if (isSuccess) {
        const configMap = new Map(configs.map(c => [c.kpiId, c]));
        setAllKpis(prev => prev.map(k => {
          const upd = configMap.get(k.kpiId);
          if (!upd) return k;
          return {
            ...k,
            selectedUnitId: upd.selectedUnitId,
            selectedUnit: k.supportedUnits.find(u => u.unit_id === upd.selectedUnitId)?.unit ?? k.selectedUnit,
          };
        }));
        setPendingConfigs(prev => { const n = new Map(prev); configs.forEach(c => n.delete(c.kpiId)); return n; });
        setShowBulkModal(false);
        showToast(`${configs.length} KPI${configs.length !== 1 ? "s" : ""} updated`);
      } else {
        showToast(data?.message || "Bulk apply failed", "error");
      }
    } catch {
      showToast("Error during bulk apply", "error");
    }
  };

  const handleSaveAll = async () => {
    if (!pendingConfigs.size) return;
    setSaving(true);
    const configs = Array.from(pendingConfigs.values());
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}saveUnitKpiSettings`,
        {},
        { configs },
        "POST"
      );
      if (isSuccess) {
        const configMap = new Map(configs.map(c => [c.kpiId, c]));
        setAllKpis(prev => prev.map(k => {
          const upd = configMap.get(k.kpiId);
          if (!upd) return k;
          return {
            ...k,
            selectedUnitId: upd.selectedUnitId,
            selectedUnit: k.supportedUnits.find(u => u.unit_id === upd.selectedUnitId)?.unit ?? k.selectedUnit,
          };
        }));
        setPendingConfigs(new Map());
        showToast(`${configs.length} change${configs.length !== 1 ? "s" : ""} saved`);
      } else {
        showToast(data?.message || "Failed to save", "error");
      }
    } catch {
      showToast("Error saving changes", "error");
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (kpi) => {
    const pending = pendingConfigs.get(kpi.kpiId);
    setEditingKpi(pending ? { ...kpi, ...pending } : kpi);
  };

  const hasPending = pendingConfigs.size > 0;
  const kpisWithUnits = allKpis.filter(k => k.hasUnits).length;

  if (loading) {
    return (
      <div style={{ padding: 100, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <Spinner size={32} color="#3f88a5" />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading KPI unit settings…</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#f1f5f9", minHeight: "100vh" }}>
      <style>{`
        @keyframes ukpi-spin { to { transform: rotate(360deg) } }
        @keyframes ukpi-toast-in { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
        * { box-sizing: border-box; }
        select:focus { outline: none; }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "error" ? "#dc2626" : "#16a34a",
          color: "#fff", padding: "11px 18px", borderRadius: 10,
          zIndex: 2000, fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          animation: "ukpi-toast-in 0.25s ease",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {toast.type === "error" ? "✕" : "✓"} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{
        background: "#fff", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: "linear-gradient(to bottom, #3f88a5, #2563eb)" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>KPI Unit Settings</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {allKpis.length} KPI{allKpis.length !== 1 ? "s" : ""} · {kpisWithUnits} with units
              {hasPending && !isViewOnly && (
                <span style={{ color: "#d97706", fontWeight: 600 }}>
                  {" "}· {pendingConfigs.size} unsaved
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SimpleDropdown
            options={fyData.map(fy => ({
              value: fy.id,
              label: `${fy.financial_year_value}${fy.id === latestFY?.id ? " (Current)" : ""}`,
            }))}
            value={selectedFY?.id || ""}
            onChange={val => {
              const found = fyData.find(fy => fy.id === Number(val));
              if (found) setSelectedFY({ id: found.id, name: found.financial_year_value, financial_year_value: found.financial_year_value });
            }}
            isAlert={selectedFY?.id !== latestFY?.id}
            style={{ width: 180 }}
          />
          {hasPending && !isViewOnly && (
            <button onClick={() => setPendingConfigs(new Map())} style={{
              padding: "8px 16px", borderRadius: 8, border: "1.5px solid #fecaca",
              background: "#fff5f5", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#ef4444",
            }}>
              Discard
            </button>
          )}
          {!isViewOnly && (
            <button
              onClick={handleSaveAll}
              disabled={!hasPending || saving}
              style={{
                background: hasPending ? "#3f88a5" : "#e2e8f0",
                color: hasPending ? "#fff" : "#94a3b8",
                border: "none", padding: "9px 22px", borderRadius: 8,
                cursor: hasPending ? "pointer" : "default",
                fontSize: 13, fontWeight: 600,
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: hasPending ? "0 2px 8px rgba(63,136,165,0.35)" : "none",
              }}
            >
              {saving ? <><Spinner size={12} color="#fff" /> Saving…</> : "Save All"}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>

        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, background: "#e2e8f0", padding: 3, borderRadius: 10, width: "fit-content", marginBottom: 14 }}>
          {[
            { key: "list", label: "All KPIs", count: allKpis.length },
            { key: "selected", label: "Selected", count: selectedKpisCount }
          ].map(tab => (
            <button key={tab.key} onClick={() => setKpiView(tab.key)} style={{
              padding: "7px 20px", border: "none", borderRadius: 8,
              background: kpiView === tab.key ? "#fff" : "transparent",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              color: kpiView === tab.key ? "#0f172a" : "#64748b",
              display: "flex", alignItems: "center", gap: 7,
              boxShadow: kpiView === tab.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none", transition: "all 0.15s"
            }}>
              {tab.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                background: kpiView === tab.key ? (tab.key === "selected" ? "#dbeafe" : "#f1f5f9") : "#d1d5db",
                color: kpiView === tab.key ? (tab.key === "selected" ? "#1d4ed8" : "#475569") : "#94a3b8"
              }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Filter + search panel */}
        <div style={{ background: "#fff", padding: "16px", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 0 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8" }}>⌕</span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search KPIs by title…"
                style={{
                  width: "100%", padding: "9px 12px 9px 32px",
                  border: "1.5px solid #e2e8f0", borderRadius: 9,
                  fontSize: 13, outline: "none", color: "#1e293b", background: "#fff",
                }}
                onFocus={e => e.target.style.borderColor = "#93c5fd"}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>
            {displayKpis.filter(k => k.hasUnits).length > 0 && !isViewOnly && (
              <button
                onClick={() => setShowBulkModal(true)}
                style={{
                  padding: "9px 18px", borderRadius: 8, border: "1.5px solid #bae6fd",
                  background: "#f0f9ff", cursor: "pointer", fontSize: 12, fontWeight: 700,
                  color: "#0369a1", display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                }}
              >
                ⚡ Bulk Apply
                <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 8, background: "#bae6fd", color: "#1e40af" }}>
                  {displayKpis.filter(k => k.hasUnits).length}
                </span>
              </button>
            )}
          </div>
          <TagFilterBar allQuestions={allKpis} filterSteps={filterSteps} onFilterStepsChange={setFilterSteps} />
        </div>

        {/* KPI list */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 120px auto auto",
            padding: "9px 18px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9",
          }}>
            {["KPI Title", "Unit", "", ""].map((h, i) => (
              <span key={i} style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</span>
            ))}
          </div>

          {displayKpis.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "#94a3b8" }}>
                {search || filterSteps.some(s => s.values?.size) ? "No KPIs match the current filters." : (kpiView === "selected" ? "No KPIs selected/configured yet." : "No KPI data available.")}
              </div>
            </div>
          ) : (
            <div style={{ maxHeight: 620, overflowY: "auto" }}>
              {displayKpis.map((kpi, idx) => (
                <KpiUnitRow
                  key={kpi.kpiId}
                  kpi={kpi}
                  pendingConfig={pendingConfigs.get(kpi.kpiId) ?? null}
                  onConfigure={openEditor}
                  onQuickUnitChange={handleQuickUnitChange}
                  isLast={idx === displayKpis.length - 1}
                  disabled={isViewOnly}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pending footer */}
        {hasPending && !isViewOnly && (
          <div style={{
            marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10, padding: "12px 20px",
          }}>
            <div style={{ fontSize: 12, color: "#92400e", fontWeight: 600 }}>
              {pendingConfigs.size} unsaved change{pendingConfigs.size !== 1 ? "s" : ""} — click Save All to persist.
            </div>
            <button onClick={handleSaveAll} disabled={saving} style={{
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: "#3f88a5", color: "#fff", cursor: "pointer",
              fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 8,
            }}>
              {saving ? <><Spinner size={11} color="#fff" /> Saving…</> : "Save All"}
            </button>
          </div>
        )}
      </div>

      {editingKpi && (
        <UnitEditorModal kpi={editingKpi} onClose={() => setEditingKpi(null)} onSave={handleModalSave} />
      )}
      {showBulkModal && (
        <BulkApplyModal filteredKpis={filteredKpis} onClose={() => setShowBulkModal(false)} onApply={handleBulkApply} />
      )}
    </div>
  );
}